import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { X, Megaphone, Loader2, Send } from 'lucide-react';
import { Notifications } from '@/lib/api';

/**
 * Lider → tüm aktif kullanıcılara toplu duyuru. Eski Dashboard.js "Toplu Mesaj
 * Gönder" özelliğinin karşılığı. Gönderince backend WS ile herkese anında push.
 */
export function BroadcastModal({ onClose }: { onClose: () => void }) {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [sent, setSent] = useState(false);

  const mut = useMutation({
    mutationFn: () => Notifications.broadcast(body.trim(), title.trim() || undefined),
    onSuccess: () => {
      setSent(true);
      setTimeout(onClose, 900);
    },
  });

  const canSend = body.trim().length > 0 && !mut.isPending && !sent;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onMouseDown={onClose}>
      <div className="w-full max-w-[460px] bg-surface rounded-2xl shadow-2xl overflow-hidden" onMouseDown={(e) => e.stopPropagation()}>
        <header className="flex items-center gap-2.5 px-4 py-3 border-b border-slate-100">
          <span className="w-8 h-8 rounded-lg bg-brand-100 text-brand-700 flex items-center justify-center">
            <Megaphone size={15} />
          </span>
          <div className="flex-1">
            <div className="text-[14px] font-semibold text-slate-900">Toplu Duyuru</div>
            <div className="text-[11.5px] text-slate-500">Tüm aktif temsilcilere anında bildirim</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1"><X size={18} /></button>
        </header>

        <div className="p-4 space-y-3">
          <div>
            <label className="text-[11.5px] font-medium text-slate-600 block mb-1">Başlık <span className="text-slate-400">(opsiyonel)</span></label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={120}
              placeholder="Örn. Vardiya Hatırlatması"
              className="w-full px-3 py-2 rounded-lg text-[13px] bg-slate-50 ring-1 ring-slate-200 focus:ring-brand-300 outline-none"
            />
          </div>
          <div>
            <label className="text-[11.5px] font-medium text-slate-600 block mb-1">Mesaj</label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              maxLength={1000}
              rows={4}
              placeholder="Duyuru metnini yaz…"
              className="w-full px-3 py-2 rounded-lg text-[13px] bg-slate-50 ring-1 ring-slate-200 focus:ring-brand-300 outline-none resize-none"
            />
            <div className="text-[10.5px] text-slate-400 text-right mt-0.5">{body.length}/1000</div>
          </div>

          {mut.isError && (
            <div className="text-[12px] text-red-600 bg-red-50 ring-1 ring-red-200 rounded-lg px-3 py-2">
              {(mut.error as any)?.response?.data?.message ?? 'Gönderilemedi'}
            </div>
          )}

          <button
            disabled={!canSend}
            onClick={() => mut.mutate()}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-[13px] font-medium bg-brand-500 hover:bg-brand-600 text-[#08140d] shadow-[0_4px_12px_-3px_rgba(255,79,31,0.5)] disabled:opacity-40 disabled:cursor-not-allowed transition active:scale-[0.98]"
          >
            {sent ? 'Gönderildi ✓' : mut.isPending ? <><Loader2 size={15} className="animate-spin" /> Gönderiliyor…</> : <><Send size={15} /> Herkese Gönder</>}
          </button>
        </div>
      </div>
    </div>
  );
}
